import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToClass } from 'class-transformer';

import { environment } from '../../../environments/environment';
import { IFieldDefinitionLogic } from '../interfaces/ifield-definition-logic.interface';
import { FieldDefinition } from '../models/form-builder/field-definition.model';
import { AddRowAboveFieldDefinitionCustomLogic } from '../models/form-builder/field-defs/add-row-above-field-def-logic';
import { AddRowBelowFieldDefinitionCustomLogic } from '../models/form-builder/field-defs/add-row-below-field-def-logic';
import { CascadingDropdownFieldDefLogic } from '../models/form-builder/field-defs/cascading-dropdown-field-def-logic';
import { CascadingDropdownVirtualFieldDefLogic } from '../models/form-builder/field-defs/cascading-dropdown-virtual-field-def-logic';
import { CheckboxFieldDefLogic } from '../models/form-builder/field-defs/checkbox-field-def-logic';
import { CurrencyFieldDefLogic } from '../models/form-builder/field-defs/currency-field-def-logic';
import { DateFieldDefLogic } from '../models/form-builder/field-defs/date-field-def-logic';
import { DecimalFieldDefLogic } from '../models/form-builder/field-defs/decimal-field-def-logic';
import { DefaultFieldDefinitionClientLogic } from '../models/form-builder/field-defs/default-field-def-client-logic';
import { DropdownFieldDefLogic } from '../models/form-builder/field-defs/dropdown-field-def-logic';
import { GridCascadingDropdownFieldDefLogic } from '../models/form-builder/field-defs/grid-cascading-dropdown-field-def-logic';
import { HideFromFieldDefinitionCustomLogic } from '../models/form-builder/field-defs/hide-from-field-def-logic';
import { IntegerFieldDefLogic } from '../models/form-builder/field-defs/integer-field-def-logic';
import { NumericFieldDefLogic } from '../models/form-builder/field-defs/numeric-field-def-logic';
import { MultiCheckboxFieldDefLogic } from '../models/form-builder/field-defs/multi-checkbox-field-def-logic';
import { MultiDropdownFieldDefLogic } from '../models/form-builder/field-defs/multi-dropdown-field-def-logic';
import { ParagraphFieldDefLogic } from '../models/form-builder/field-defs/paragraph-field-def-logic';
import { RadioButtonsFieldDefLogic } from '../models/form-builder/field-defs/radio-buttons-field-def-logic';
import { RichTextFieldDefLogic } from '../models/form-builder/field-defs/rich-text-field-def-logic';
import { ShortTextFieldDefLogic } from '../models/form-builder/field-defs/short-text-field-def-logic';
import { ShowToFieldDefinitionCustomLogic } from '../models/form-builder/field-defs/show-to-field-def-logic';
import { EditorNameFieldDefLogic } from '../models/form-builder/field-defs/editor-name-field-def-logic';
import { LastEditedDateFieldDefLogic } from '../models/form-builder/field-defs/last-edited-date-field-def-logic';

import {
    AddRowAboveFieldType,
    AddRowBelowFieldType,
    CascadingDropDownFieldType,
    CascadingDropDownVirtualFieldType,
    CheckboxFieldType,
    CurrencyFieldType,
    DateFieldType,
    DecimalFieldType,
    DropDownFieldType,
    FavoriteFieldType,
    FlexibleSelectionFieldType,
    FolderNameFieldType,
    FolderPathFieldType,
    FormInstanceNameFieldType,
    FormInstanceIdFieldType,
    FormTemplateInstancesFieldType,
    FormTemplateNameFieldType,
    FormulaFieldType,
    GridCascadingDropDownFieldType,
    GridFieldType,
    HideFromFieldType,
    HTMLLinkFieldType,
    IdFieldType,
    IntegerFieldType,
    NumericFieldType,
    MarkDataReadOnlyFieldType,
    MultiCheckboxFieldType,
    MultiDropDownFieldType,
    ParagraphFieldType,
    RadioButtonsFieldType,
    RichTextFieldType,
    ShortTextFieldType,
    ShowToFieldType,
    ValueConcatenatorFieldType,
    UserRolesFieldType,
    WorkflowStateFieldType,
    EditorNameFieldType,
    LastEditedDateFieldType,
    FlexFieldButtonsFieldType,
    ContactsFieldType,
    AttachmentFieldType
} from '../models/form-builder/form-field-types';
import { QueryOptions } from '../query-options';
import { CollectApiServiceBase } from './collect-api-base.service';
import { FavoriteFieldDefLogic } from '../models/form-builder/field-defs/favorite-field-def-logic';
import { FlexibleSelectionFieldDefLogic } from '../models/form-builder/field-defs/flexible-selection-field-def-logic';
import { FlexFieldButtonsFieldLogic } from '../models/form-builder/field-defs/flex-field-buttons-field-logic';
import { FolderNameFieldDefinitionLogic } from '../models/form-builder/field-defs/folder-name-field-def-logic';
import { FolderPathFieldDefinitionLogic } from '../models/form-builder/field-defs/folder-path-field-def-logic';
import { FormInstanceNameFieldDefinitionLogic } from '../models/form-builder/field-defs/form-instance-name-field-def-logic';
import { FormInstanceIdFieldDefinitionLogic } from '../models/form-builder/field-defs/form-instance-id-field-def-logic';
import { FormTemplateNameFieldDefinitionLogic } from '../models/form-builder/field-defs/form-template-name-field-def-logic';
import { FormulaFieldDefLogic } from '../models/form-builder/field-defs/formula-field-def-logic';
import { GridFieldDefLogic } from '../models/form-builder/field-defs/grid-field-def-logic';
import { ContactsFieldDefLogic } from '../models/form-builder/field-defs/contacts-field-def-logic';
import { AttachmentsFieldDefLogic } from '../models/form-builder/field-defs/attachments-field-def-logic';
import { HtmlLinkFieldDefLogic } from '../models/form-builder/field-defs/html-link-field-def-logic';
import { IdMetadataFieldDefLogic } from '../models/form-builder/field-defs/id-metadata-field-def-logic';
import { MarkDataAsReadOnlyFieldDefinitionCustomLogic } from '../models/form-builder/field-defs/mark-data-as-read-only-field-def-logic';
import { FormTemplateInstancesFieldDefLogic } from '../models/form-builder/field-defs/template-instances-field-def-logic';
import { ValueConcatenatorFieldDefLogic } from '../models/form-builder/field-defs/value-concatenator-field-def-logic';
import { UserRolesFieldDefinitionLogic } from '../models/form-builder/field-defs/user-roles-field-def-logic';
import { WorkflowStateFieldDefinitionLogic } from '../models/form-builder/field-defs/workflow-state-field-def-logic';
import { ProgressIndicatorService } from './progress-indicator.service';
import { AttachmentService } from './attachment.service';
import { DomSanitizer } from '@angular/platform-browser';

// Define type-safe map types.
interface IFieldDefinitionNameToFieldDefinition {
    [fieldName: string]: FieldDefinition;
}
interface IFieldDefinitionNameToDisplayName {
    [fieldName: string]: string;
}
export interface IFieldDefintionToCustomLogicHandler {
    [fieldDefinitionClassName: string]: IFieldDefinitionLogic;
}

// Define/implement field definition custom logic classes.
// Define the custom logic handler that does nothing (works for most field definition classes).
//
// Note:  these classes have been moved into their own files and are imported above.

// Implement this file's service class.
@Injectable(
    { providedIn: 'root' }
)
export class FieldDefinitionService extends CollectApiServiceBase<FieldDefinition> {
    // Properties.
    private static staticCachedFieldDefs: FieldDefinition[] = null;
    private static staticMapOfCachedFieldDefs: IFieldDefinitionNameToFieldDefinition = null;

    private static staticMapOfFieldDefinitionNamesToDisplayNames: IFieldDefinitionNameToDisplayName = {};
    private static staticMapOfCustomLogicHandlers: IFieldDefintionToCustomLogicHandler = {};

    // Constructor.
    public constructor(http: HttpClient,
        public progressIndicatorService: ProgressIndicatorService,
        private attachmentService: AttachmentService,
        private sanitizer: DomSanitizer
    ) {
        super(http, progressIndicatorService, environment.apiUrl, 'fieldDefinition', FieldDefinition);

        this.loadCustomLogicHandlers();

        return;
    }

    // Getter/setter methods.
    public get fieldDefinitionsLoaded(): boolean {
        let loaded = ((this.cachedFieldDefs != null) && (this.cachedFieldDefs.length > 0));
        return loaded;
    }

    // Getter/setters methods for accessing static variables.
    private get cachedFieldDefs(): FieldDefinition[] {
        return FieldDefinitionService.staticCachedFieldDefs;
    }
    private get mapOfCachedFieldDefs(): IFieldDefinitionNameToFieldDefinition {
        return FieldDefinitionService.staticMapOfCachedFieldDefs;
    }
    private get mapOfFieldDefinitionNamesToDisplayNames(): IFieldDefinitionNameToDisplayName {
        return FieldDefinitionService.staticMapOfFieldDefinitionNamesToDisplayNames;
    }
    private get mapOfCustomLogicHandlers(): IFieldDefintionToCustomLogicHandler {
        return FieldDefinitionService.staticMapOfCustomLogicHandlers;
    }

    private set cachedFieldDefs(value: FieldDefinition[]) {
        FieldDefinitionService.staticCachedFieldDefs = value;
    }
    private set mapOfCachedFieldDefs(value: IFieldDefinitionNameToFieldDefinition) {
        FieldDefinitionService.staticMapOfCachedFieldDefs = value;
    }
    private set mapOfFieldDefinitionNamesToDisplayNames(value: IFieldDefinitionNameToDisplayName) {
        FieldDefinitionService.staticMapOfFieldDefinitionNamesToDisplayNames = value;
    }
    private set mapOfCustomLogicHandlers(value: IFieldDefintionToCustomLogicHandler) {
        FieldDefinitionService.staticMapOfCustomLogicHandlers = value;
    }

    //formatResponse(data: FieldDefinition): FieldDefinition {
    //    let obj = Object.assign(new FieldDefinition(), data);

    //    return obj;
    //}
    public formatResponse(data: FieldDefinition): FieldDefinition {
        let obj = plainToClass(FieldDefinition, data);

        return (obj);
    }

    public getAll(queryOptions?: QueryOptions): Promise<FieldDefinition[]> {
        let result: Promise<FieldDefinition[]> = null;

        if (this.cachedFieldDefs == null) {
            result = new Promise<FieldDefinition[]>((resolve, reject) => {
                super.getAll(queryOptions).then(responseFieldDefs => {
                    this.cacheFieldDefinitions(responseFieldDefs);

                    resolve(responseFieldDefs);
                });
            });
        } else {
            result = new Promise<FieldDefinition[]>((resolve, reject) => {
                resolve(this.cachedFieldDefs);
            });
        }

        return result;
    }

    public getMetadataFields(): Promise<FieldDefinition[]> {
        let result: Promise<FieldDefinition[]> = null;

        result = new Promise<FieldDefinition[]>((resolve, reject) => {
            this.getAll().then(allFieldDefs => {
                let metadataFieldDefs: FieldDefinition[] = allFieldDefs.filter(fd => fd.isMetadataField);

                resolve(metadataFieldDefs);
            });
        });

        return result;
    }

    // Implement getAllResponseReady(), called by the base class's getAll() method.
    protected getAllResponseReady(response: FieldDefinition[]): void {
        // Have we initialized the map of field definition names to display names?
        let mapKeys: string[] = Object.keys(this.mapOfFieldDefinitionNamesToDisplayNames);
        if (mapKeys.length == 0) {
            // Initialize the map now.
            for (let iFieldDef: number = 0; iFieldDef < response.length; iFieldDef++) {
                let fieldDefinition: FieldDefinition = response[iFieldDef];

                this.mapOfFieldDefinitionNamesToDisplayNames[fieldDefinition.className] = fieldDefinition.displayName;
            }
        }
    }

    public getFieldTypeDisplayName(fieldDefinitionName: string, throwExceptionOnError: boolean = true): string {
        let displayName: string = this.mapOfFieldDefinitionNamesToDisplayNames[fieldDefinitionName];

        if ((displayName == undefined) && throwExceptionOnError) {
            let errorMsg: string = `FieldDefinitionService.getFieldTypeDisplayName():  could not find a display name for field definition class '${fieldDefinitionName}.`;
            throw (errorMsg);
        }

        return (displayName);
    }

    // Note:  it might make sense to rework the following method to return a Promise<FieldDefinition> 
    //        result, though we would have to analyze how this method is being used before doing so.
    public getFieldDefinition(fieldDefinitionClassName: string, throwExceptionOnFailure: boolean = true): FieldDefinition {
        let fieldDefinition: FieldDefinition = null;

        if (this.mapOfCachedFieldDefs != null) {
            fieldDefinition = this.mapOfCachedFieldDefs[fieldDefinitionClassName];

            if (fieldDefinition.customLogicHandler == null)
                fieldDefinition.customLogicHandler = this.getFieldClientLogicHandler(fieldDefinitionClassName);
        }

        if ((fieldDefinition == null) && throwExceptionOnFailure)
            throw `FieldDefinitionService.getFieldDefinition():  could not get a field definition for field class '${fieldDefinitionClassName}'.`;

        return fieldDefinition;
    }

    // PJH - 4/2/2026 - VNEXT-1749 - to handle vritual child fields of Attachment Grids, needed to allow this method
    // to take more information (the "hint") to assist with selecting the right logic handler
    public getFieldClientLogicHandler(fieldDefinitionClassName: string, hint: string = null): IFieldDefinitionLogic {
        // If the hint indicates the field is a virtual child field of an Attachment Field, set fieldDefinitionClassName to Attachment class
        if (this.isNameOfStandardAttachmentVirtualField(hint)) {
            fieldDefinitionClassName = AttachmentFieldType;
        } else if (this.isContactEmailField(hint)) {
            fieldDefinitionClassName = ContactsFieldType;
        }

        let customLogicHandler: IFieldDefinitionLogic = this.mapOfCustomLogicHandlers[fieldDefinitionClassName];

        if (customLogicHandler == null)
            customLogicHandler = new DefaultFieldDefinitionClientLogic();

        return customLogicHandler;
    }

    private isContactEmailField(fieldName: string) {
        return fieldName == 'contactemaildonotedit';
    }

    private isNameOfStandardAttachmentVirtualField(fieldName: string) {
        return fieldName?.includes('standard_attachment_');
    }

    // Helper methods.
    private cacheFieldDefinitions(responseFieldDefs: FieldDefinition[]): void {
        this.cachedFieldDefs = responseFieldDefs;

        this.mapOfCachedFieldDefs = {};

        if (responseFieldDefs != null){
            for (let index: number = 0; index < responseFieldDefs.length; index++){
                let responseFieldDef: FieldDefinition = responseFieldDefs[index];

                this.mapOfCachedFieldDefs[responseFieldDef.className] = responseFieldDef;
            }
        }
    }

    private loadCustomLogicHandlers(): void {
        this.mapOfCustomLogicHandlers[AddRowAboveFieldType] = new AddRowAboveFieldDefinitionCustomLogic();
        this.mapOfCustomLogicHandlers[AddRowBelowFieldType] = new AddRowBelowFieldDefinitionCustomLogic();
        this.mapOfCustomLogicHandlers[ShowToFieldType] = new ShowToFieldDefinitionCustomLogic();
        this.mapOfCustomLogicHandlers[HideFromFieldType] = new HideFromFieldDefinitionCustomLogic();
        this.mapOfCustomLogicHandlers[CascadingDropDownFieldType] = new CascadingDropdownFieldDefLogic();
        this.mapOfCustomLogicHandlers[CascadingDropDownVirtualFieldType] = new CascadingDropdownVirtualFieldDefLogic();
        this.mapOfCustomLogicHandlers[GridCascadingDropDownFieldType] = new GridCascadingDropdownFieldDefLogic();
        this.mapOfCustomLogicHandlers[CheckboxFieldType] = new CheckboxFieldDefLogic();
        this.mapOfCustomLogicHandlers[DropDownFieldType] = new DropdownFieldDefLogic();
        this.mapOfCustomLogicHandlers[MultiDropDownFieldType] = new MultiDropdownFieldDefLogic();
        this.mapOfCustomLogicHandlers[MultiCheckboxFieldType] = new MultiCheckboxFieldDefLogic();
        this.mapOfCustomLogicHandlers[RadioButtonsFieldType] = new RadioButtonsFieldDefLogic();
        this.mapOfCustomLogicHandlers[DecimalFieldType] = new DecimalFieldDefLogic();
        this.mapOfCustomLogicHandlers[CurrencyFieldType] = new CurrencyFieldDefLogic();
        this.mapOfCustomLogicHandlers[IntegerFieldType] = new IntegerFieldDefLogic();
        this.mapOfCustomLogicHandlers[NumericFieldType] = new NumericFieldDefLogic();
        this.mapOfCustomLogicHandlers[DateFieldType] = new DateFieldDefLogic();
        this.mapOfCustomLogicHandlers[ShortTextFieldType] = new ShortTextFieldDefLogic();
        this.mapOfCustomLogicHandlers[RichTextFieldType] = new RichTextFieldDefLogic();
        this.mapOfCustomLogicHandlers[ParagraphFieldType] = new ParagraphFieldDefLogic();
        this.mapOfCustomLogicHandlers[FormulaFieldType] = new FormulaFieldDefLogic(this);
        this.mapOfCustomLogicHandlers[ValueConcatenatorFieldType] = new ValueConcatenatorFieldDefLogic(this); 
        //TEAMS-835: KLW - Needed for the HTML Link form field
        this.mapOfCustomLogicHandlers[HTMLLinkFieldType] = new HtmlLinkFieldDefLogic();
        this.mapOfCustomLogicHandlers[MarkDataReadOnlyFieldType] = new MarkDataAsReadOnlyFieldDefinitionCustomLogic();
        this.mapOfCustomLogicHandlers[GridFieldType] = new GridFieldDefLogic();
        this.mapOfCustomLogicHandlers[ContactsFieldType] = new ContactsFieldDefLogic();
        this.mapOfCustomLogicHandlers[AttachmentFieldType] = new AttachmentsFieldDefLogic(this.attachmentService, this.sanitizer);
        this.mapOfCustomLogicHandlers[FlexibleSelectionFieldType] = new FlexibleSelectionFieldDefLogic();
        this.mapOfCustomLogicHandlers[FlexFieldButtonsFieldType] = new FlexFieldButtonsFieldLogic();

        // Metadata field definition logic classes:
        this.mapOfCustomLogicHandlers[UserRolesFieldType] = new UserRolesFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[WorkflowStateFieldType] = new WorkflowStateFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[FolderNameFieldType] = new FolderNameFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[FolderPathFieldType] = new FolderPathFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[FormInstanceNameFieldType] = new FormInstanceNameFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[FormInstanceIdFieldType] = new FormInstanceIdFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[FormTemplateNameFieldType] = new FormTemplateNameFieldDefinitionLogic();
        this.mapOfCustomLogicHandlers[IdFieldType] = new IdMetadataFieldDefLogic();
        this.mapOfCustomLogicHandlers[FavoriteFieldType] = new FavoriteFieldDefLogic();

        this.mapOfCustomLogicHandlers[FormTemplateInstancesFieldType] = new FormTemplateInstancesFieldDefLogic();
        this.mapOfCustomLogicHandlers[EditorNameFieldType] = new EditorNameFieldDefLogic();
        this.mapOfCustomLogicHandlers[LastEditedDateFieldType] = new LastEditedDateFieldDefLogic();

    }
}
